import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useState, useEffect } from "react";
import {
  useCreateMoneyTransferRequestMutation,
  useGetMoneyTransferRequestsByOrderQuery,
} from "store/queries/moneyTransfers";
import styled from "styled-components";
import { ITransfer } from "types/MoneyTransfers";
import { IOrder } from "types/Order/Order";

import { MoneyTransferRequestsList } from "./components/MoneyTransferRequestStatus";
import { SingleTransferForm } from "./components/SingleTransferForm";
import { mapFormToRequestBody } from "./utils/helpers";

const StyledDialog = styled(Dialog)`
  .p-dialog-title {
    color: #5b4dfd;
  }
`;

export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

const WarningBanner = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  padding: ${sizer(3)};
  margin-bottom: ${sizer(4)};
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};

  .warning-icon {
    color: #856404;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .warning-content {
    flex: 1;

    .warning-title {
      font-weight: 600;
      color: #856404;
      margin-bottom: ${sizer(1)};
    }

    .warning-text {
      color: #664d03;
      font-size: 0.9rem;
      margin: 0;
    }
  }
`;

const SectionHeader = styled.h4`
  color: #5b4dfd;
  margin: 0 0 ${sizer(3)} 0;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
`;

export function MoneyTransferDialog({
  showDialog,
  setShowDialog,
  order,
  transfers,
  setTransfers,
  onUpdateOrder,
}: {
  showDialog: boolean;
  setShowDialog: any;
  order: IOrder;
  transfers: ITransfer[];
  setTransfers: any;
  onUpdateOrder: () => void;
}) {
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { current: toastElement } = useContext(ToastContext);
  const [useCreateMoneyTransferRequest] =
    useCreateMoneyTransferRequestMutation();

  // Fetch transfer history when dialog opens
  const { data: requestsData, refetch: refetchRequests } =
    useGetMoneyTransferRequestsByOrderQuery(order.id, {
      skip: !showDialog,
    });

  // Check for any existing transfers
  const existingTransfers = requestsData?.data || [];

  // Refetch data when dialog opens
  useEffect(() => {
    if (showDialog) {
      refetchRequests();
    }
  }, [showDialog, refetchRequests]);
  const handleCreateMoneyTransfers = async () => {
    const requestBody = mapFormToRequestBody({
      orderId: order.id,
      moneyTransfers: transfers,
      note: notes?.trim()?.length ? notes.trim() : undefined,
    });
    // Transfers without a From, a To or an amount above $0.00 are dropped from the body, so an
    // empty body means there is nothing to send: say so rather than posting an empty request.
    if (!requestBody.moneyTransfers.length) {
      toastElement?.show({
        severity: "error",
        summary: "Nothing to transfer",
        detail:
          "Choose who to transfer from and to, and an amount greater than $0.00 that the From party has available.",
      });
      return;
    }
    setLoading(true);

    // Always use asynchronous money transfer request endpoint
    useCreateMoneyTransferRequest(requestBody)
      .unwrap()
      .then(async (response) => {
        if (response.data?.error) {
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: response.data.error,
          });
          setLoading(false);
          return;
        }

        // Show success message with scheduling information
        const { totalRequests, firstScheduledAt, lastScheduledAt, message } =
          response.data;
        const now = new Date();
        const firstDelay = Math.ceil(
          (new Date(firstScheduledAt).getTime() - now.getTime()) / (1000 * 60)
        );

        let detail = message;
        if (totalRequests > 1) {
          const lastDelay = Math.ceil(
            (new Date(lastScheduledAt).getTime() - now.getTime()) / (1000 * 60)
          );
          detail = `${totalRequests} money transfer requests created. First processes ${
            firstDelay > 1 ? `in ${firstDelay} minutes` : "shortly"
          }, last processes in ${lastDelay} minutes.`;
        } else if (firstDelay > 1) {
          detail = `Money transfer request created and scheduled to process in ${firstDelay} minutes`;
        } else {
          detail =
            "Money transfer request created and will be processed shortly";
        }

        toastElement?.show({
          severity: "success",
          summary: `${totalRequests} Money Transfer Request${
            totalRequests === 1 ? "" : "s"
          } Created`,
          detail,
          life: 8000, // Longer display time for multiple requests
        });
        setLoading(false);
        setShowDialog(false);
        setNotes("");
        onUpdateOrder();
        // Refetch transfer requests after creating a new one
        refetchRequests();
      })
      .catch((error) => {
        setLoading(false);
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail:
            error.data?.message || "Failed to create money transfer request",
        });
      });
  };

  const footerContent = (
    <div className="flex justify-content-center">
      <Button
        label="Transfer"
        onClick={handleCreateMoneyTransfers}
        autoFocus
        loading={loading}
      />
    </div>
  );

  const handleAddAnotherTransfer = () => {
    setTransfers((prev: any) => [
      ...prev,
      {
        selectedPartyToTransferFrom: null,
        selectedPartyToTransferTo: null,
        selectedAmountToTransfer: 0,
      },
    ]);
  };

  const handleRemoveTransfer = (index: number) => {
    const transferCopy = [...transfers];
    transferCopy.splice(index, 1);
    setTransfers(transferCopy);
  };

  return (
    <StyledDialog
      header="Money Transfers"
      visible={showDialog}
      style={{ width: "60vw", minHeight: "600px" }}
      blockScroll
      onHide={() => {
        setShowDialog(false);
        setTransfers([
          {
            selectedPartyToTransferFrom: null,
            selectedPartyToTransferTo: null,
            selectedAmountToTransfer: 0,
          },
        ]);
      }}
      footer={footerContent}
    >
      <div className="flex flex-column">
        {/* Warning Banner and History Section - only shown when transfers exist */}
        {existingTransfers.length > 0 && (
          <>
            <WarningBanner>
              <i className="pi pi-exclamation-triangle warning-icon" />
              <div className="warning-content">
                <div className="warning-title">Existing Transfers Found</div>
                <p className="warning-text">
                  There {existingTransfers.length === 1 ? "is" : "are"}{" "}
                  {existingTransfers.length} existing money transfer
                  {existingTransfers.length === 1 ? "" : "s"} for this order.
                  Please review {existingTransfers.length === 1 ? "it" : "them"}{" "}
                  below before creating a new transfer to avoid duplicates.
                </p>
              </div>
            </WarningBanner>

            <MoneyTransferRequestsList
              orderId={order.id}
              data={existingTransfers}
              compact
            />

            <Divider />
          </>
        )}

        {/* Create New Transfer Section - only show header when there are existing transfers */}
        {transfers.map((transfer, i) => (
          <SingleTransferForm
            key={`${i}_order_transfer`}
            transfer={transfer}
            transfers={transfers}
            setTransfers={setTransfers}
            order={order}
            index={i}
            handleRemoveTransfer={handleRemoveTransfer}
            showRemoveButton={transfers.length > 1}
          />
        ))}
        <StyledButton
          data-testid="remove-transfer"
          icon="pi pi-plus-circle"
          className="p-button-text p-button-primary p-0 mb-0"
          style={{ alignSelf: "end" }}
          onClick={handleAddAnotherTransfer}
        >
          <p className="small-text">Add Another</p>
        </StyledButton>
        <div className="w-full mt-3" style={{ textAlign: "left" }}>
          <InputTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Notes"
            className="w-full"
            style={{ textAlign: "left", width: "100%" }}
          />
        </div>
      </div>
    </StyledDialog>
  );
}
